import { paths } from '@/app/router/paths';
import { surveyReopenedAfterSending, type Order, type OrderAccess } from '@/entities/order';

import { surveyActionCopy as copy } from './copy';
import type { LocalStop } from './MyDayModel';

/**
 * De ene handeling die bij een keuring hoort: starten, hervatten, of het rapport
 * openen. Eén functie, drie schermen — de lijst Keuringen, de kop van één keuring
 * en Mijn dag — zodat een keuring nooit op het ene scherm "starten" zegt en op het
 * andere iets anders.
 *
 * `note` is geen foutmelding maar een mededeling: wie de keuring heeft. Ze staat er
 * ook naast een knop, want "Zelf overnemen en starten" zonder te zeggen van wie is
 * geen keuze.
 */
export type SurveyAction =
    /** De keuring nu beginnen (`POST /orders/{order}/start`). */
    | { kind: 'start'; label: string }
    /** Hetzelfde, maar hij staat op naam van een collega die nog niet begon. */
    | { kind: 'takeOver'; label: string; note: string }
    /** Alleen navigeren: de opname loopt al, of het rapport is er. */
    | { kind: 'open'; label: string; to: string }
    /** Niets te doen hier, en dit is waarom. */
    | { kind: 'note'; note: string }
    /** Niets te doen, en niets te melden: deze keuring gaat je niet aan. */
    | { kind: 'none' };

/**
 * Na de opname is er niets meer te starten; het rapport is dan de volgende stap. Behalve
 * als een opname na verzending weer open staat: dan is die opname weer het werk.
 */
const FINISHED = ['completed', 'invoiced', 'cancelled'];

export function surveyActionFor(order: Order, access: OrderAccess): SurveyAction {
    /*
     * De eerste dienst die op locatie wordt ingevuld. `delivery` komt van de API en
     * staat er ook al vóór de opname bestaat, dus dit weet ook van een nieuwe keuring
     * of er iets te starten valt. Alleen een energielabel: niets.
     *
     * Staat een opname na verzending weer open, dan is dat de opname die telt: een
     * keuring met twee opnames heropent er misschien maar één.
     */
    const reopened = surveyReopenedAfterSending(order);
    const survey = reopened
        ? order.services.find(
              (service) =>
                  service.delivery === 'survey' &&
                  (service.inspection?.status === 'in_progress' ||
                      service.inspection?.status === 'completed'),
          )
        : order.services.find((service) => service.delivery === 'survey');

    if (survey === undefined || (FINISHED.includes(order.status) && !reopened)) {
        return { kind: 'none' };
    }

    if (order.status === 'draft_report' || survey.inspection?.status === 'submitted') {
        return { kind: 'open', label: copy.report, to: paths.report(order.uuid) };
    }

    if (!access.perform) {
        return { kind: 'none' };
    }

    const mine = order.assignee === null || order.assignee.uuid === access.userUuid;
    const name = order.assignee?.name ?? copy.aColleague;
    const running = survey.inspection !== null && survey.inspection.status !== 'not_started';

    if (running) {
        const target =
            reopened && survey.inspection !== null
                ? { uuid: survey.inspection.uuid, service: survey.service }
                : order.inspection;

        return mine && target !== null
            ? { kind: 'open', label: copy.resume, to: paths.inspectionFor(target) }
            : { kind: 'note', note: copy.busyElsewhere(name) };
    }

    if (mine) {
        return { kind: 'start', label: copy.start };
    }

    /* Overnemen is toewijzen; wie dat niet mag, leest alleen van wie de keuring is. */
    return access.assign
        ? { kind: 'takeOver', label: copy.takeOver, note: copy.assignedTo(name) }
        : { kind: 'note', note: copy.assignedTo(name) };
}

/**
 * Dezelfde handeling voor een stop op Mijn dag.
 *
 * Mijn dag leest de lokale opslag en niet de API: de keuring is al ingepland en aan
 * jou toegewezen, dus er valt niets toe te wijzen en niets over te nemen. Starten is
 * hier de lokale start ({@link import('@/entities/inspection').inspectionMutations.start}),
 * die ook zonder verbinding werkt — vandaar `open` met een pad en niet `start`.
 */
export function surveyActionForStop(stop: LocalStop): SurveyAction {
    if (stop.inspectionUuid === null) {
        return { kind: 'note', note: copy.noSurvey };
    }

    const to = stop.energyLabel
        ? paths.energyPerformance(stop.inspectionUuid)
        : paths.survey(stop.inspectionUuid);

    if (stop.submitted) {
        return { kind: 'open', label: copy.view, to };
    }

    return { kind: 'open', label: stop.resuming ? copy.resume : copy.start, to };
}
